import React from "react";
import { useQuery } from "wasp/client/operations";
import {
  getLostFoundItemById,
  deleteLostFound,
  getVisibleOptionalFields,
} from "wasp/client/operations";
import { useAction } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  LOST_FOUND_REWARD_FIELD_KEY,
  LOST_FOUND_CATEGORY_FIELD_KEY,
} from "../section-visibility/optionalFieldConstants";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../components/layout";

import {
  MapPin,
  Calendar,
  Phone,
  Mail,
  DollarSign,
  Tag,
  Edit,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";
import { getCommunityBreadcrumbDropdownItems } from "../community/constants";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";

export default function ViewLostFoundPage() {
  useRequireSectionVisible("showLostFound");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const deleteLostFoundFn = useAction(deleteLostFound);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Fetch the item data
  const {
    data: item,
    isLoading,
    error,
  } = useQuery(getLostFoundItemById, { itemId: itemId! });
  const { data: visibleOptionalFields } = useQuery(getVisibleOptionalFields);
  const optionalByKey =
    (visibleOptionalFields as { byKey?: Record<string, boolean> } | undefined)
      ?.byKey ?? {};
  const showRewardField = optionalByKey[LOST_FOUND_REWARD_FIELD_KEY] !== false;
  const showCategoryField =
    optionalByKey[LOST_FOUND_CATEGORY_FIELD_KEY] !== false;

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/lost-found`);
    } else {
      navigate("/lost-found");
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this item? This action cannot be undone.",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await deleteLostFoundFn({ itemId: itemId! });
      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/lost-found`);
      } else {
        navigate("/lost-found");
      }
    } catch (err) {
      alert("Failed to delete item. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to view lost/found items.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading item...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Item Not Found
            </h3>
            <p className="text-gray-600">
              The item you're looking for doesn't exist.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getTypeColor = (type: string) => {
    return type === "LOST_ITEM"
      ? "bg-red-100 text-red-800"
      : "bg-green-100 text-green-800";
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      ELECTRONICS: "bg-blue-100 text-blue-800",
      CLOTHING: "bg-purple-100 text-purple-800",
      ACCESSORIES: "bg-pink-100 text-pink-800",
      DOCUMENTS: "bg-yellow-100 text-yellow-800",
      KEYS: "bg-gray-100 text-gray-800",
      BOOKS: "bg-orange-100 text-orange-800",
      SPORTS: "bg-green-100 text-green-800",
      OTHER: "bg-indigo-100 text-indigo-800",
    };
    return (
      colors[category as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Community",
      dropdownItems: getCommunityBreadcrumbDropdownItems(
        selectedLocation?.locationCode ?? null,
      ),
    },
    {
      label: "Lost & Found",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/lost-found`
        : "/lost-found",
    },
  ];

  const isAdmin = user?.isAdmin;

  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={
                selectedLocation?.locationCode
                  ? `/locations/${selectedLocation.locationCode}/lost-found/${item.id}/edit`
                  : `/lost-found/${item.id}/edit`
              }
              icon={Edit}
            >
              Edit Item
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
              disabled={isDeleting}
              icon={Trash2}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                  Deleting...
                </>
              ) : (
                "Delete Item"
              )}
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Type"
            value={item.type === "LOST_ITEM" ? "Lost Item" : "Found Item"}
          />
          {showCategoryField && (
            <DetailField
              label="Category"
              value={item.metadata?.category || "N/A"}
            />
          )}
          <DetailField
            label="Posted by"
            value={item.author?.name || item.author?.email || "N/A"}
          />
          <DetailField
            label="Posted on"
            value={new Date(item.createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      {/* Item Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-content-text">
                {item.title}
              </h2>
              <SubscribeButton contentId={item.id} />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getTypeColor(
                  item.type,
                )}`}
              >
                {item.type === "LOST_ITEM" ? "Lost Item" : "Found Item"}
              </span>
              {showCategoryField && (
                <span
                  className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                    item.metadata?.category,
                  )}`}
                >
                  {item.metadata?.category}
                </span>
              )}
            </div>
          </div>

          {showRewardField && item.metadata?.reward != null && (
            <div className="text-2xl font-bold text-green-600">
              ${item.metadata.reward}
            </div>
          )}
        </div>

        <p className="text-content-text text-lg leading-relaxed">
          {item.description}
        </p>
      </div>

      {/* Item Details */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Item Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {item.metadata?.locationFound && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Location {item.type === "LOST_ITEM" ? "Lost" : "Found"}
                  </div>
                  <div className="text-gray-900">
                    {item.metadata.locationFound}
                  </div>
                </div>
              </div>
            )}

            {item.metadata?.dateFound && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Date {item.type === "LOST_ITEM" ? "Lost" : "Found"}
                  </div>
                  <div className="text-gray-900">
                    {new Date(item.metadata.dateFound).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </div>
                </div>
              </div>
            )}

            {(item.metadata?.contactPhone ||
              item.metadata?.contactEmail ||
              item.metadata?.contactInfo) && (
                <div className="flex flex-col gap-2">
                  {item.metadata?.contactPhone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">
                          Phone
                        </div>
                        <div className="text-gray-900">
                          {item.metadata.contactPhone}
                        </div>
                      </div>
                    </div>
                  )}
                  {item.metadata?.contactEmail && (
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">
                          Email
                        </div>
                        <div className="text-gray-900">
                          {item.metadata.contactEmail}
                        </div>
                      </div>
                    </div>
                  )}
                  {!item.metadata?.contactPhone &&
                    !item.metadata?.contactEmail &&
                    item.metadata?.contactInfo && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-500">
                            Contact Information
                          </div>
                          <div className="text-gray-900">
                            {item.metadata.contactInfo}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {showCategoryField && (
              <div className="flex items-start gap-3">
                <Tag className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Category
                  </div>
                  <div className="text-gray-900">{item.metadata?.category}</div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-500">
                  Reported
                </div>
                <div className="text-gray-900">
                  {new Date(item.createdAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-gray-400 mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">
                  Reported by
                </div>
                <div className="text-gray-900">
                  {item.author.name || item.author.email}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      {(item.metadata?.contactPhone ||
        item.metadata?.contactEmail ||
        item.metadata?.contactInfo) && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {item.type === "LOST_ITEM"
                ? "Found this item?"
                : "Is this your item?"}
            </h3>
            <p className="text-gray-600 mb-4">
              {item.type === "LOST_ITEM"
                ? "If you found this item, please contact the owner using the information above."
                : "If this is your lost item, please contact the finder using the information above."}
            </p>
            <div className="flex flex-col gap-1 text-blue-600">
              {item.metadata?.contactPhone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span className="font-medium">
                    {item.metadata.contactPhone}
                  </span>
                </div>
              )}
              {item.metadata?.contactEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="font-medium">
                    {item.metadata.contactEmail}
                  </span>
                </div>
              )}
              {!item.metadata?.contactPhone &&
                !item.metadata?.contactEmail &&
                item.metadata?.contactInfo && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span className="font-medium">
                      {item.metadata.contactInfo}
                    </span>
                  </div>
                )}
            </div>
          </div>
        )}
    </IndividualViewPageLayout>
  );
}
